import { cn } from "@/lib/cn";
import type { RepairStatus } from "@/lib/supabase";
import { Check, MessageCircle, Video, Wrench } from "lucide-react";

type Step = {
  key: "ai" | "video" | "instructed" | "completed";
  label: string;
  icon: typeof Check;
  state: "done" | "active" | "pending";
};

type Props = {
  status: RepairStatus;
  hadVideoTriage: boolean;
};

export function RepairTimeline({ status, hadVideoTriage }: Props) {
  const cancelled = status === "cancelled";
  const completed = status === "completed";
  const instructed = status === "contractor_assigned" || status === "in_progress" || completed;

  const steps: Step[] = [];
  steps.push({
    key: "ai",
    label: "AI triage",
    icon: MessageCircle,
    state: "done",
  });
  if (hadVideoTriage) {
    steps.push({
      key: "video",
      label: "Video triage",
      icon: Video,
      state: "done",
    });
  }
  steps.push({
    key: "instructed",
    label: "Instructed",
    icon: Wrench,
    state: instructed ? "done" : "pending",
  });
  if (completed) {
    steps.push({
      key: "completed",
      label: "Completed",
      icon: Check,
      state: "done",
    });
  }
  // Mark the last "done" as the active one (visual emphasis).
  for (let i = steps.length - 1; i >= 0; i--) {
    const step = steps[i];
    if (step?.state === "done") {
      step.state = "active";
      break;
    }
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {cancelled ? (
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-200 text-xs uppercase tracking-wider">
          Cancelled
        </span>
      ) : (
        steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border transition",
                  s.state === "active"
                    ? "bg-accent text-accent-on border-accent shadow-glow"
                    : s.state === "done"
                      ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
                      : "bg-bg-card text-ink-dim border-line",
                )}
              >
                <Icon size={12} />
                {s.label}
              </span>
              {i < steps.length - 1 ? (
                <span
                  className={cn(
                    "h-px w-4",
                    steps[i + 1]?.state === "pending" ? "bg-line" : "bg-emerald-500/40",
                  )}
                />
              ) : null}
            </div>
          );
        })
      )}
    </div>
  );
}
